import { useEffect, useState } from "react";
import { storage } from "../../state/storage.js";
import { ProvidersSection } from "./ProvidersSection.js";
import { SecuritySection } from "./SecuritySection.js";
import { PersonaSection } from "./PersonaSection.js";
import { PresetSection } from "./PresetSection.js";
import { UsageLedgerSection } from "./UsageLedgerSection.js";
import { ScriptsSection } from "./ScriptsSection.js";
import { JsScriptsSection } from "./JsScriptsSection.js";
import { BackupSection } from "./BackupSection.js";
import { SyncSection } from "./SyncSection.js";
import { IdentitySection } from "./IdentitySection.js";
import { useT } from "../../i18n/use-t.js";
import type { Settings } from "@roqeet/core";

/**
 * Settings view. Surfaces the locked decisions as real toggles:
 *  - identity tier (decision #8)
 *  - sync incl. key-inclusion default off (decisions #8/#15/#20)
 *  - performance: incremental markdown opt-in, non-streaming fallback (#14)
 *  - age gate / NSFW visibility (decision: age-gate + NSFW)
 * Settings are a single row; writes go straight through the storage port.
 */
export function SettingsView() {
  const { t } = useT();
  const [settings, setSettings] = useState<Settings | undefined>(undefined);

  useEffect(() => {
    storage.getSettings().then(setSettings);
  }, []);

  const update = async (patch: Partial<Settings>) => {
    const current = await storage.getSettings();
    const next = { ...current, ...patch } as Settings;
    setSettings(next);
    await storage.putSettings(next);
  };

  if (!settings) {
    return <p className="p-4 text-sm text-ink-faint">{t("common.loading")}</p>;
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 scroll-touch">
      <div className="mx-auto max-w-xl space-y-6">
        <Section title={t("settings.display.title")}>
          <Row label={t("settings.theme")}>
            <select
              className="rounded-lg border border-line bg-bg-soft px-2 py-1 text-sm text-ink"
              value={settings.theme}
              onChange={(e) => update({ theme: e.target.value as Settings["theme"] })}
            >
              <option value="system">{t("settings.theme.system")}</option>
              <option value="dark">{t("settings.theme.dark")}</option>
              <option value="light">{t("settings.theme.light")}</option>
            </select>
          </Row>
        </Section>

        <Section title={t("settings.perf.title")}>
          <Toggle
            label={t("settings.perf.incMarkdown")}
            hint={t("settings.perf.incMarkdownHint")}
            checked={settings.performance.incrementalMarkdown}
            onChange={(v) =>
              update({ performance: { ...settings.performance, incrementalMarkdown: v } })
            }
          />
          <Toggle
            label={t("settings.perf.forceNonStream")}
            hint={t("settings.perf.forceNonStreamHint")}
            checked={settings.performance.forceNonStreaming}
            onChange={(v) =>
              update({ performance: { ...settings.performance, forceNonStreaming: v } })
            }
          />
          <Toggle
            label={t("settings.perf.reducedMotion")}
            hint={t("settings.perf.reducedMotionHint")}
            checked={settings.performance.reducedMotion}
            onChange={(v) =>
              update({ performance: { ...settings.performance, reducedMotion: v } })
            }
          />
        </Section>

        <PersonaSection />

        <PresetSection />

        <UsageLedgerSection />

        <ScriptsSection />

        <JsScriptsSection />

        <ProvidersSection />

        <SecuritySection />

        <BackupSection />

        <SyncSection />

        <IdentitySection />

        <Section title={t("settings.content.title")}>
          <Row label={t("settings.content.ageVerify")}>
            <span className="text-sm text-ink-soft">
              {settings.ageVerified
                ? t("settings.content.verified")
                : t("settings.content.unverified")}
            </span>
          </Row>
          <Toggle
            label={t("settings.content.showNsfw")}
            hint={t("settings.content.showNsfwHint")}
            checked={!!settings.showNsfw}
            onChange={(v) => {
              if (v && !settings.ageVerified) {
                // 연령 게이트(결정 #23): 미확인 상태에서 켜면 확인을 거친다.
                const ok = confirm(t("settings.content.ageConfirm"));
                if (!ok) return; // 거부 시 토글하지 않음
                void update({ ageVerified: true, showNsfw: true });
                return;
              }
              void update({ showNsfw: v });
            }}
          />
          {settings.ageVerified && (
            <Row label={t("settings.content.ageReset")}>
              <button
                className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                onClick={() => update({ ageVerified: false, showNsfw: false })}
              >
                {t("settings.content.reset")}
              </button>
            </Row>
          )}
        </Section>

        <p className="pt-2 text-xs text-ink-faint">{t("settings.securityNotice")}</p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {title}
      </h2>
      <div className="divide-y divide-line rounded-xl border border-line bg-bg-soft">
        {children}
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
      <span className="text-sm text-ink">{label}</span>
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  // 치수(트랙/노브)는 인라인 스타일로 고정한다. Tailwind가 드문 유틸리티(w-11 등)를
  // 프로덕션 빌드에서 누락시키면 트랙이 collapse되어 노브가 밖으로 튀어나오는 버그가
  // 있었다. 색상만 클래스로 두고 기하학은 인라인으로 박아 purge 영향을 원천 차단한다.
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-3">
      <div className="min-w-0">
        <div className="text-sm text-ink">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-ink-faint">{hint}</div>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={[
          "relative shrink-0 rounded-full transition-colors",
          checked ? "bg-accent" : "bg-bg-raised",
        ].join(" ")}
        style={{ width: 44, height: 24, marginTop: 2 }}
      >
        <span
          className={[
            "absolute rounded-full transition-transform",
            checked ? "bg-white" : "border border-line bg-bg-soft shadow-sm",
          ].join(" ")}
          style={{
            width: 20,
            height: 20,
            top: 2,
            left: 2,
            transform: checked ? "translateX(20px)" : "translateX(0)",
          }}
        />
      </button>
    </div>
  );
}
